import React, { PureComponent } from 'react';
import { Flex } from '../../../common/Flex';
import FlightTable, {
    FLIGHT_ACTIONS_COLUMN,
    FLIGHT_SELECTED_COLUMN,
    FlightColumn,
    StandardFlightColumn,
    StandardFlightColumnV2,
} from '../../../table/FlightTable';
import KeyUtils from '../../../../common/util/KeyUtils';
import { FlightDto } from 'app/model/CORE';
import { SettingFilled } from '@ant-design/icons';
import { MULTIPLE_SELECTION, MultipleSelection } from 'app/common/table/ManagedTable';
import memoizeOne from 'memoize-one';
import { SelectionLabel } from '../../../common/SelectionLabel';
import './Flights.less';
import { Button, Tag } from 'antd';
import {
    DEFAULT_CFMU_TYPES,
    FlightListFilterSettings,
    FlightsSettingsContent,
    FlightsSettingsTitle,
} from './FlightsSettings';
import { Filter } from 'app/common/table/Filter';
import { ClosablePopover } from '../../../common/ClosablePopover';
import Utils from '../../../../common/util/Utils';
import {
    CfmuTypesFilter,
    DelayedFlightsOnlyFilter,
    FlightTextFilter,
    SelectedFlightsOnlyFilter,
} from './FlightFilters';
import { TableUtils } from 'app/common/util/TableUtils';
import { TextFilterInput } from 'app/common/input/TextFilterInput';
import AppContext from '../../../../common/context/AppContext';
import { UpdateFlightSelection } from 'app/common/eventbus/Events';
import ConditionalRender from 'app/common/components/ConditionalRender';
import AppConstants from 'app/common/context/AppConstants';

const SIMPLE_FLIGHT_COLUMNS_V2: FlightColumn[] = [
    FLIGHT_SELECTED_COLUMN,
    StandardFlightColumnV2.ARCID,
    StandardFlightColumn.ATYPE,
    StandardFlightColumnV2.ADEP,
    StandardFlightColumnV2.ADES,
    StandardFlightColumnV2.IOBT,
    StandardFlightColumn.CFMU_TYPE,
    StandardFlightColumnV2.ATOT_TTOT,
    StandardFlightColumn.CTOT,
    StandardFlightColumn.ATOT,
    StandardFlightColumnV2.xTA,
    StandardFlightColumn.MPR,
    StandardFlightColumnV2.DELAY,
    StandardFlightColumn.NUM_OVERLOAD_TVS,
    FLIGHT_ACTIONS_COLUMN,
];

/*const ENTRY_EXIT_FLIGHT_COLUMNS: FlightColumn[] = [
    FLIGHT_SELECTED_COLUMN, StandardFlightColumn.ENTRY_TIME, StandardFlightColumn.EXIT_TIME, StandardFlightColumn.ARCID, StandardFlightColumn.ATYPE, StandardFlightColumn.ADEP,
    StandardFlightColumn.ADES, StandardFlightColumn.IOBT, StandardFlightColumn.ATOT_TTOT, StandardFlightColumn.CTOT, StandardFlightColumn.ATOT, StandardFlightColumn.xTA,
    StandardFlightColumn.MPR, StandardFlightColumn.DELAY, StandardFlightColumn.NUM_OVERLOAD_TVS, FLIGHT_ACTIONS_COLUMN
]*/

interface FlightsProps {
    flights?: Array<FlightDto>;
    showEntryExitColumns?: boolean;

    selection?: Array<FlightDto>;
    onSelectionChange?: (selection: Array<FlightDto>) => void;

    filterSettings: FlightListFilterSettings;
    onFilterSettingsChange: (filterSettings: FlightListFilterSettings) => void;
}

export class Flights extends PureComponent<FlightsProps> {
    private settingsPopoverRef = React.createRef<ClosablePopover>();
    static CFMU_FLIGHT_TYPES = [];

    constructor(props: FlightsProps) {
        super(props);
        this.initEventHandlers();
    }

    initEventHandlers = () => {
        AppContext.eventBus.register(UpdateFlightSelection.NAME, () => {
            this.props.onFilterSettingsChange({ ...this.props.filterSettings, selectedOnly: true });
        });
    };

    selection = memoizeOne((selectedFlights: FlightDto[]): MultipleSelection<FlightDto> => {
        return {
            selectionMode: MULTIPLE_SELECTION,
            onSelectionChange: this.handleSelectionChange,
            selectedItems:
                selectedFlights /*FlightTableBase.getUpdatedMultipleSelection(this.props.selection, this.props.flights)*/,
        };
    });

    handleSettingsChange = (settings: FlightListFilterSettings) => {
        this.props.onFilterSettingsChange(settings);
        this.settingsPopoverRef.current.hide();
    };

    handleSelectionChange = (selection: Array<FlightDto>) => {
        this.props.onSelectionChange?.(selection);
    };

    handleTextFilterChange = (text: string) => {
        this.props.onFilterSettingsChange({ ...this.props.filterSettings, textFilter: text });
    };

    handleSelectAll = (selection: Array<FlightDto>) => {
        const newSelection = Utils.copyAndAddAllIfNeeded(this.props.selection, selection);
        return () => this.handleSelectionChange(newSelection);
    };

    handleClearSelection = () => {
        this.handleSelectionChange([]);
    };

    getCombinedFilter = memoizeOne((filterSettings: FlightListFilterSettings) => (): Filter<FlightDto> => {
        const { textFilter, selectedOnly, delayedOnly, delayThreshold, cfmuTypes } = filterSettings;
        const filters: Array<Filter<FlightDto>> = [
            textFilter ? new FlightTextFilter(textFilter) : undefined,
            selectedOnly ? new SelectedFlightsOnlyFilter(this.props.selection) : undefined,
            delayedOnly ? new DelayedFlightsOnlyFilter(delayThreshold) : undefined,
            new CfmuTypesFilter(cfmuTypes),
        ]
            .filter(filter => Utils.isExists(filter))
            .map(filter => filter.filter());
        return {
            keep(elem: FlightDto): boolean {
                return filters.every(filter => filter.keep(elem));
            },
        };
    });

    renderFilterTags = () => {
        const { selectedOnly, delayedOnly, delayThreshold, cfmuTypes } = this.props.filterSettings;

        return [
            <ConditionalRender key="selected-only" condition={selectedOnly}>
                <Tag
                    closable
                    onClose={() =>
                        this.props.onFilterSettingsChange({
                            ...this.props.filterSettings,
                            selectedOnly: false,
                        })
                    }>
                    Selected only
                </Tag>
            </ConditionalRender>,
            <ConditionalRender key="delayed-only" condition={delayedOnly}>
                <Tag
                    closable
                    onClose={() =>
                        this.props.onFilterSettingsChange({
                            ...this.props.filterSettings,
                            delayedOnly: false,
                        })
                    }>{`Delayed only > ${delayThreshold}mn`}</Tag>
            </ConditionalRender>,
            Flights.CFMU_FLIGHT_TYPES.filter(type => !cfmuTypes.includes(type)).map(type => {
                return (
                    <Tag
                        key={type}
                        style={{ textDecoration: 'line-through' }}
                        closable
                        onClose={() =>
                            this.props.onFilterSettingsChange({
                                ...this.props.filterSettings,
                                cfmuTypes: [...this.props.filterSettings.cfmuTypes, type],
                            })
                        }>
                        {type}
                    </Tag>
                );
            }),
        ];
    };

    updateCfmuFlightTypes = memoizeOne((flights: FlightDto[]) => {
        const availableTypes = Array.from(new Set(flights?.map(f => f.cfmuFlightType)));
        Flights.CFMU_FLIGHT_TYPES = DEFAULT_CFMU_TYPES.filter(type => availableTypes.includes(type));
    });

    render() {
        this.updateCfmuFlightTypes(this.props.flights);
        const filteredFlights = TableUtils.getFilteredData(
            this.props.flights,
            this.getCombinedFilter(this.props.filterSettings)(),
        );
        const nbFlights = filteredFlights?.length || 0;
        const nbFlightsSelected = this.props.selection?.length || 0;
        const selectionLabelVisible = nbFlightsSelected > 0;
        return (
            <Flex direction="vertical" className="flights overflow detailed-subpanel">
                <Flex className="detailed-subpanel-header" size="2em">
                    <Flex className="detailed-subpanel-header-left">
                        <SelectionLabel
                            visible={selectionLabelVisible}
                            title={`${nbFlightsSelected} flight${nbFlightsSelected > 1 ? 's' : ''} selected`}
                            subTitle={`${nbFlights} in total`}
                            onSelectAll={this.handleSelectAll(filteredFlights)}
                            onClearSelection={this.handleClearSelection}
                            style={{ marginTop: 0, marginBottom: 0 }}>
                            <div style={{ height: '1em', alignSelf: 'center' }}>
                                <span className="detailed-subpanel-title">FLIGHTS</span>
                                <span className="detailed-subpanel-title-info">{nbFlights}</span>
                            </div>
                        </SelectionLabel>
                    </Flex>
                    <Flex className="detailed-subpanel-header-right">
                        <ConditionalRender key="delayed-only" condition={nbFlights > 0}>
                            {nbFlightsSelected < nbFlights ? (
                                <Button onClick={this.handleSelectAll(filteredFlights)}>SELECT ALL</Button>
                            ) : (
                                <Button onClick={this.handleClearSelection}>DESELECT ALL</Button>
                            )}
                        </ConditionalRender>
                        {this.renderFilterTags()}
                        <TextFilterInput
                            value={this.props.filterSettings.textFilter}
                            onTextFilterChange={this.handleTextFilterChange}
                        />
                        <span className="detailed-subpanel-header-right" style={{ fontSize: '1.5em' }}>
                            <ClosablePopover
                                overlayClassName="flight-settings-wrapper"
                                key="settings-action"
                                ref={this.settingsPopoverRef}
                                trigger="click"
                                placement="bottom"
                                title={<FlightsSettingsTitle />}
                                content={
                                    <FlightsSettingsContent
                                        settings={this.props.filterSettings}
                                        onChange={this.handleSettingsChange}
                                        onClose={() => this.settingsPopoverRef.current.hide()}
                                    />
                                }>
                                <SettingFilled onClick={() => this.settingsPopoverRef.current.show()} />
                            </ClosablePopover>
                        </span>
                    </Flex>
                </Flex>
                <Flex>
                    <FlightTable
                        data={filteredFlights}
                        columns={SIMPLE_FLIGHT_COLUMNS_V2}
                        keyProvider={KeyUtils.FLIGHT_KEY_PROVIDER}
                        noDataMessage={'No active flights in the monitored period'}
                        selection={this.selection(this.props.selection)}
                        defaultSorting={
                            /*(this.props.showEntryExitColumns
                            ? [{columnName: StandardFlightColumn.ENTRY_TIME.header, direction: 'asc'}]
                            : */ [{ columnName: StandardFlightColumn.IOBT.header, direction: 'asc' }] /*)*/
                        }
                    />
                </Flex>
            </Flex>
        );
    }
}
